// Удаляем неиспользуемый импорт
// import { supabase } from './supabase';
import * as CryptoJS from 'crypto-js';

interface TelegramLoginUser {
    id: string; // Telegram User ID
    first_name: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    auth_date: number;
    hash: string;
    [key: string]: string | number | undefined; // Индексная сигнатура для доступа по строковому ключу
}

interface TelegramVerifyResult {
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const BOT_TOKEN = import.meta.env.VITE_TELEGRAM_BOT_TOKEN as string;

/**
 * Локальная проверка данных Telegram (без обращения к серверу)
 * Используется как запасной вариант если сервер недоступен
 */
const verifyTelegramDataLocally = (userData: TelegramLoginUser): boolean => {
    try {
        console.log('Локальная проверка данных Telegram...');
        
        // Проверка наличия необходимых данных
        if (!userData || !userData.hash || !userData.auth_date || !userData.id) {
            console.error('Неполные данные для проверки');
            return false;
        }
        
        // Проверка наличия токена бота
        if (!BOT_TOKEN) {
            console.error('Отсутствует VITE_TELEGRAM_BOT_TOKEN в переменных окружения');
            return false;
        }
        
        // Проверка актуальности auth_date (не старше 24 часов)
        const now = Math.floor(Date.now() / 1000);
        if (now - userData.auth_date > 86400) {
            console.error('Устаревшие данные авторизации:', userData.auth_date, 'текущее время:', now);
            return false;
        }
        
        // Создаем копию данных без hash поля для проверки
        const { hash, ...data } = userData;
        
        // Сортируем ключи и создаем строку для проверки
        const dataCheckString = Object.keys(data)
            .sort()
            .map((key) => `${key}=${data[key]}`)
            .join('\n');
            
        console.log('Строка для проверки:', dataCheckString);
        
        // Создаем хеш от токена бота
        const secretKey = CryptoJS.SHA256(BOT_TOKEN);
        
        // Создаем HMAC подпись
        const signature = CryptoJS.HmacSHA256(dataCheckString, secretKey);
        const calculatedHash = signature.toString(CryptoJS.enc.Hex);
        
        console.log('Вычисленный хеш:', calculatedHash);
        console.log('Полученный хеш:', hash);
        
        // Сравниваем хеши
        return calculatedHash === hash;
    } catch (error) {
        console.error('Ошибка при локальной проверке данных Telegram:', error);
        return false;
    }
};

/**
 * Проверяет, что данные от Telegram действительно пришли от Telegram
 * с использованием Edge Function или локальной проверки
 */
const verifyTelegramData = async (userData: TelegramLoginUser): Promise<TelegramVerifyResult> => {
    try {
        console.log('Отправка данных для проверки на сервере:', userData);
        console.log('SUPABASE_URL для API-запроса:', SUPABASE_URL);
        
        // Проверяем данные на валидность перед отправкой
        if (!userData || !userData.id || !userData.hash || !userData.auth_date) {
            console.error('Неполные данные пользователя Telegram:', userData);
            return {
                success: false,
                error: 'Некорректные данные аутентификации Telegram'
            };
        }
        
        // Сначала пробуем проверить локально
        const isLocallyValid = verifyTelegramDataLocally(userData);
        if (isLocallyValid) {
            console.log('Данные успешно верифицированы локально');
            // Возвращаем данные в формате, аналогичном серверному ответу
            return {
                success: true,
                message: "Telegram data verified successfully (locally)",
                data: {
                    telegram_id: userData.id,
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    username: userData.username,
                    photo_url: userData.photo_url,
                }
            };
        }
        
        console.log('Локальная верификация не удалась, пробуем через сервер...');
        
        // Проверяем наличие анонимного ключа Supabase
        if (!SUPABASE_ANON_KEY) {
            console.error('Отсутствует VITE_SUPABASE_ANON_KEY в переменных окружения');
            return {
                success: false,
                error: 'Ошибка конфигурации: отсутствует ключ авторизации'
            };
        }
        
        // Используем данные как есть, без модификации, чтобы сохранить точный формат
        // для корректной проверки хеша на сервере
        console.log('Отправляем оригинальные данные без модификации:', userData);
        
        // Отправляем запрос на функцию верификации с добавлением заголовка авторизации
        const fetchResponse = await fetch(`${SUPABASE_URL}/functions/v1/verify-telegram`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify(userData),
        });

        console.log('Статус ответа от функции verify-telegram:', fetchResponse.status);
        
        // Получаем текст ответа для дополнительного логирования
        const responseText = await fetchResponse.text();
        console.log('Текст ответа от сервера verify-telegram:', responseText);
        
        // Преобразуем текст в JSON
        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            console.error('Ошибка парсинга JSON ответа:', e);
            return {
                success: false,
                error: 'Некорректный формат ответа от сервера'
            };
        }
        
        console.log('Распарсенный результат проверки:', result);
        
        if (!fetchResponse.ok) {
            throw new Error(result.error || 'Ошибка при проверке данных Telegram');
        }
        
        return result;
    } catch (error) {
        console.error('Ошибка при проверке данных Telegram:', error);
        return {
            success: false, 
            error: error instanceof Error ? error.message : 'Неизвестная ошибка'
        };
    }
};

/**
 * Обрабатывает авторизацию пользователя через Telegram
 */
export const handleTelegramAuth = async (userData: TelegramLoginUser) => {
    try {
        console.log('handleTelegramAuth начал работу с данными:', userData);
        
        // Проверка наличия всех необходимых полей
        if (!userData || !userData.id || !userData.hash || !userData.auth_date) {
            console.error('Отсутствуют обязательные поля в данных Telegram:', userData);
            return {
                success: false, 
                error: 'Неполные данные аутентификации Telegram'
            };
        }
        
        // Сначала проверяем данные с помощью Edge Function
        const verifyResult = await verifyTelegramData(userData);
        
        console.log('Результат верификации Telegram данных:', verifyResult);
        
        // Проверка структуры ответа
        if (typeof verifyResult !== 'object' || verifyResult === null) {
            console.error("Неожиданный формат ответа:", verifyResult);
            throw new Error('Неверный формат ответа от сервера');
        }
        
        if (!verifyResult.success) {
            throw new Error(verifyResult.error || 'Данные Telegram недействительны');
        }
        
        // Получаем верифицированные данные пользователя
        const telegramUser = verifyResult.data;
        
        console.log('Получены верифицированные данные:', telegramUser);
        console.log('API URL для создания пользователя:', `${SUPABASE_URL}/functions/v1/manage-users`);
        
        // Проверяем наличие необходимых данных для создания пользователя
        if (!telegramUser || !telegramUser.telegram_id) {
            console.error('Отсутствуют необходимые данные пользователя:', telegramUser);
            throw new Error('Недостаточно данных для создания пользователя');
        }
        
        // Теперь создаем/обновляем пользователя в базе данных
        const createUserResponse = await fetch(`${SUPABASE_URL}/functions/v1/manage-users`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
                action: 'createOrUpdate',
                userData: telegramUser
            }),
        });
        
        console.log('Статус ответа от функции manage-users:', createUserResponse.status);
        
        // Получаем текст ответа для логирования
        const createUserResponseText = await createUserResponse.text();
        console.log('Текст ответа от manage-users:', createUserResponseText);
        
        // Парсим JSON
        let createUserResult;
        try {
            createUserResult = JSON.parse(createUserResponseText);
        } catch (e) {
            console.error('Ошибка парсинга JSON ответа от manage-users:', e);
            throw new Error('Некорректный формат ответа от сервера при создании пользователя');
        }
        
        if (!createUserResponse.ok) {
            throw new Error(createUserResult.error || 'Ошибка при создании пользователя');
        }
        
        console.log('Пользователь создан/обновлен:', createUserResult);
        
        // Сохраняем данные пользователя в localStorage
        const user = createUserResult.user || telegramUser;
        localStorage.setItem('user', JSON.stringify(user));
        
        return {
            success: true,
            user
        };
    } catch (error) {
        console.error('Ошибка при авторизации через Telegram:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Неизвестная ошибка'
        };
    }
};

/**
 * Получает данные авторизованного пользователя из localStorage
 */
export const getCurrentUser = () => {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;

    try {
        return JSON.parse(userJson);
    } catch (error) {
        console.error('Ошибка при парсинге данных пользователя:', error);
        return null;
    }
};

/**
 * Выход пользователя из системы
 */
export const logout = () => {
    localStorage.removeItem('user');
}; 